import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  CheckCircle, 
  FileText, 
  Users, 
  Star, 
  Shield, 
  Target, 
  ArrowRight,
  Calendar,
  MapPin,
  Award,
  Zap,
  TrendingUp,
  Globe2,
  Lightbulb
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '@/styles/animations.css';

const BusinessIncubator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      title: "Startup Support",
      description: "Comprehensive support for new business ventures",
      icon: Lightbulb
    },
    {
      title: "Mentorship",
      description: "Expert guidance from successful entrepreneurs",
      icon: Users
    },
    {
      title: "Networking",
      description: "Connect with investors and business partners",
      icon: Globe2
    },
    {
      title: "Resources",
      description: "Access to office space, equipment, and tools",
      icon: Building2
    }
  ];

  const requirements = [
    "Innovative business idea or concept",
    "Business plan and market analysis",
    "Commitment to full-time business development",
    "Team or individual with relevant skills",
    "Financial sustainability plan",
    "Clear growth potential",
    "Willingness to participate in program activities",
    "Legal right to work in Poland"
  ];

  const process = [
    {
      step: "1",
      title: "Application",
      description: "Submit your business idea and application"
    },
    {
      step: "2",
      title: "Selection",
      description: "Review and selection by incubator committee"
    },
    {
      step: "3",
      title: "Onboarding",
      description: "Welcome and initial setup in the incubator"
    },
    {
      step: "4",
      title: "Development",
      description: "12-24 months of intensive business development"
    },
    {
      step: "5",
      title: "Graduation",
      description: "Launch your business with ongoing support"
    }
  ];

  const benefits = [
    "Professional office space and facilities",
    "Business mentorship and coaching",
    "Access to funding and investment networks",
    "Legal and administrative support",
    "Marketing and branding assistance",
    "Technology and infrastructure access",
    "Networking events and workshops",
    "Market research and analysis support",
    "International expansion opportunities",
    "Ongoing alumni network support"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-teal-500/5 via-teal-600/10 to-background py-24 sm:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))] dark:bg-grid-black/10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Building2 className="h-4 w-4" />
              Business Incubator
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-6 font-alumni animate-fade-in">
              Launch Your Business Venture
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind animate-subtle-slide-up mb-8">
              Launch your business venture in Poland with our comprehensive incubator program. 
              We provide essential support services, legal guidance, and networking opportunities 
              to help your business thrive in the Polish market.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white">
                  Apply Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Features Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Key Benefits</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              What you get with our Business Incubator program
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <CardHeader className="pb-4">
                    <div className="mx-auto bg-teal-500/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-teal-600" />
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground font-alumni">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground font-hind">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6 font-alumni">Requirements</h2>
              <p className="text-lg text-muted-foreground mb-8 font-hind">
                To join our Business Incubator program, you should meet the following criteria:
              </p>
              <div className="space-y-4">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground font-hind">{requirement}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-teal-50 rounded-2xl p-8">
              <div className="text-center">
                <div className="bg-teal-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Target className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 font-alumni">Program Duration</h3>
                <p className="text-3xl font-bold text-teal-600 mb-2">12-24 Months</p>
                <p className="text-muted-foreground font-hind">Intensive business development program</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Program Process</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Our structured 5-step process guides you from idea to successful business launch
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-teal-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-teal-600">{step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 font-alumni">{step.title}</h3>
                <p className="text-muted-foreground text-sm font-hind">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-teal-500/5 to-teal-600/10 rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">What You Get</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
                Comprehensive support and resources to accelerate your business growth
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground font-hind">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4 font-alumni">Ready to Launch Your Business?</h2>
            <p className="text-xl mb-8 opacity-90 font-hind">
              Join our incubator program and turn your business idea into a successful reality
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100">
                  Apply Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white !text-teal-400 hover:bg-white/10 hover:!text-white"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BusinessIncubator; 